import { useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Checkbox,
  EmptyState,
  Spinner
} from '../primitives';
import { supabase } from '../../lib/supabase';
import type { SubscriptionTier } from '../../lib/vm/useBillingVM';
import { TIER_DISPLAY_NAME } from './TierCard';

export interface DowngradeOverageMember {
  user_id: string;
  display_name: string | null;
  username: string | null;
  discriminator: number | null;
  permission_role_system_key: string | null;
  permission_role_name: string | null;
  function_role_label: string | null;
}

export interface DowngradeOverageStepProps {
  /** Active org id. */
  orgId: string;
  /** Tier the Owner is moving to. */
  targetTier: SubscriptionTier;
  /** Seat cap of the target tier. */
  newMax: number;
  /** Currently-occupied seats. */
  usedSeats: number;
  /** Non-Owner active members (Owner cannot be suspended) — fetched by the
   *  modal owner via `list_org_members` (Active rows only, role !== owner). */
  members: ReadonlyArray<DowngradeOverageMember>;
  /** True while the parent is still loading `members`. */
  loading?: boolean;
  /** Called when the schedule-downgrade RPC + edge function both resolve OK. */
  onScheduled: () => void;
  /** Called when the user backs out without scheduling. */
  onCancel: () => void;
  /** Resolves the `stripe-update-subscription` edge-function call so this
   *  component doesn't need to know about the VM internals. */
  updateTier: (args: {
    newTier: SubscriptionTier;
    membersToSuspend?: ReadonlyArray<string>;
  }) => Promise<{ ok: boolean; error?: string }>;
}

function displayLabel(m: DowngradeOverageMember): string {
  if (m.display_name && m.display_name.trim().length > 0) return m.display_name;
  if (m.username) {
    const suffix =
      typeof m.discriminator === 'number'
        ? `#${String(m.discriminator).padStart(4, '0')}`
        : '';
    return `@${m.username}${suffix}`;
  }
  return 'Member';
}

function roleLabel(m: DowngradeOverageMember): string | null {
  const p = m.permission_role_name ?? m.permission_role_system_key ?? null;
  const f = m.function_role_label ?? null;
  if (p && f) return `${p} · ${f}`;
  return p ?? f;
}

/**
 * Downgrade-with-overage picker. Mounted by `<BillingCheckoutModal>` when the
 * Owner picks a tier whose `max_seats < usedSeats`. Lists non-Owner active
 * members; gates Confirm on exactly `(usedSeats - newMax)` selections.
 *
 * On Confirm:
 *   1. Calls `request_tier_downgrade(p_org_id, p_new_tier, p_members_to_suspend)`
 *      via `supabase.rpc(…)`. The RPC persists the picked uuids in
 *      `subscriptions.pending_suspension_user_ids` + scheduled_tier so the
 *      Stripe-side update can re-validate against server state.
 *   2. Calls `vm.updateTier({newTier, membersToSuspend})` via the supplied
 *      `updateTier` prop, which invokes `stripe-update-subscription`. The edge
 *      function re-reads `pending_suspension_user_ids` from the row written in
 *      step 1, so the array passed here must match.
 *   3. Fires `onScheduled` so the modal closes; the `/settings/billing` page
 *      now renders the scheduled-downgrade Banner from the refetched
 *      subscription row.
 */
export function DowngradeOverageStep({
  orgId,
  targetTier,
  newMax,
  usedSeats,
  members,
  loading,
  onScheduled,
  onCancel,
  updateTier
}: DowngradeOverageStepProps) {
  const requiredSelections = Math.max(0, usedSeats - newMax);

  const [selected, setSelected] = useState<ReadonlyArray<string>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const canConfirm =
    !submitting &&
    !loading &&
    selected.length === requiredSelections &&
    members.length > 0;

  function toggle(userId: string) {
    setSelected((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      // Cap selection at requiredSelections so the user can't pick more than
      // necessary.
      if (prev.length >= requiredSelections) return prev;
      return [...prev, userId];
    });
    if (errorText) setErrorText(null);
  }

  async function onConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setErrorText(null);

    const { data: rpcData, error: rpcErr } = await supabase.rpc(
      'request_tier_downgrade',
      {
        p_org_id: orgId,
        p_new_tier: targetTier,
        p_members_to_suspend: selected
      }
    );
    if (rpcErr) {
      setSubmitting(false);
      setErrorText(
        rpcErr.message ?? "Couldn't schedule the downgrade. Please try again."
      );
      return;
    }
    // The RPC can return `{ok: false, error}` envelopes too — defensively
    // unwrap. PostgREST surfaces `setof` rows as arrays.
    const rpcRow = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    if (rpcRow && typeof rpcRow === 'object' && 'ok' in rpcRow && rpcRow.ok === false) {
      setSubmitting(false);
      const e = (rpcRow as { error?: string }).error;
      setErrorText(e ?? "Couldn't schedule the downgrade. Please try again.");
      return;
    }

    const result = await updateTier({
      newTier: targetTier,
      membersToSuspend: selected
    });
    setSubmitting(false);
    if (!result.ok) {
      setErrorText(
        result.error ?? "Couldn't schedule the downgrade. Please try again."
      );
      return;
    }
    onScheduled();
  }

  const tierLabel = TIER_DISPLAY_NAME[targetTier];

  return (
    <div
      className="set-billing-downgrade"
      data-testid="billing-downgrade-overage"
    >
      <div className="set-billing-downgrade-intro">
        <h3 className="set-billing-downgrade-heading">
          Pick members to suspend
        </h3>
        <p className="set-billing-downgrade-blurb">
          {tierLabel} fits {newMax} seat{newMax === 1 ? '' : 's'}. Your org has
          {' '}
          {usedSeats}. Pick {requiredSelections} member
          {requiredSelections === 1 ? '' : 's'} to suspend at the end of the
          current billing period.
        </p>
      </div>

      {loading ? (
        <div
          className="set-billing-downgrade-loading"
          data-testid="billing-downgrade-loading"
        >
          <Spinner size="sm" label="Loading members" />
        </div>
      ) : null}

      {!loading && members.length === 0 ? (
        <EmptyState data-testid="billing-downgrade-empty">
          No non-Owner members to suspend. Remove members from the Members page
          before downgrading.
        </EmptyState>
      ) : null}

      {!loading && members.length > 0 ? (
        <ul
          className="set-billing-downgrade-list"
          role="list"
          data-testid="billing-downgrade-list"
        >
          {members.map((m) => {
            const checked = selectedSet.has(m.user_id);
            const role = roleLabel(m);
            const disabled =
              !checked && selected.length >= requiredSelections;
            return (
              <li
                key={m.user_id}
                className="set-billing-downgrade-row"
                data-testid={`billing-downgrade-row-${m.user_id}`}
              >
                <Checkbox
                  label={displayLabel(m)}
                  hint={role ?? undefined}
                  checked={checked}
                  disabled={disabled || submitting}
                  onChange={() => toggle(m.user_id)}
                  data-testid={`billing-downgrade-check-${m.user_id}`}
                />
              </li>
            );
          })}
        </ul>
      ) : null}

      {errorText ? (
        <Banner kind="error" data-testid="billing-downgrade-error">
          {errorText}
        </Banner>
      ) : null}

      <div className="set-billing-downgrade-actions">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={submitting}
          data-testid="billing-downgrade-cancel"
        >
          Back
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={onConfirm}
          disabled={!canConfirm}
          data-testid="billing-downgrade-confirm"
        >
          {submitting
            ? 'Scheduling…'
            : `Schedule downgrade (${selected.length}/${requiredSelections})`}
        </Button>
      </div>
    </div>
  );
}
